/* /app/frontend/src/pages/communication/MailReader.jsx — iter-396B
 *
 * The minimum-viable mail reader + composer that unlocks the pilot
 * Gmail workflow.  Intentionally compact + native to ChainFlow:
 *
 *   • Slides in from the right as a fixed drawer — never steals the
 *     inbox scroll position.
 *   • Fetches the full thread via `/api/v2/mailbox/thread/{id}` when
 *     the message has a Gmail threadId; falls back to the single
 *     message otherwise.
 *   • Marks the seed message as read on mount (Gmail-style).
 *   • Renders body as HTML in a sandboxed iframe when present,
 *     else plain text with URL auto-linking.
 *   • Shows the attachment manifest per message with a signed
 *     download URL that hits `GET /api/v2/mailbox/{id}/attachments/{aid}`.
 *   • Reply / Reply-All / Forward buttons pre-fill the composer with
 *     the correct recipients + subject + a quoted body block.
 *   • Composer posts to `POST /api/v2/mailbox/send` — threading
 *     (`In-Reply-To`, `References`, `threadId`) is applied server-side
 *     from the `reply_to_email_id`.
 *
 * Explicitly out-of-scope: templates, signatures, scheduling, AI
 * drafting.  All are deferrable to later iterations.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import {
  X, Reply, ReplyAll, Forward, Paperclip, Loader2, Download,
  Send, ExternalLink,
} from "lucide-react";
import { DemoBadge, isSimulatedDemo } from "../../components/ui/DemoBadge";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";


function _fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}


function _formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


function _quoteBody(msg) {
  // Gmail-style top-quoted reply block.  Kept short — a
  // 12-hour-old operator with three windows open wants to see
  // their own draft, not scroll through nested threads.
  const when = _fmtWhen(msg.received_at);
  const who = msg.from_name ? `${msg.from_name} <${msg.from_email}>` : msg.from_email;
  const body = (msg.body_text || msg.snippet || "").split("\n").map((l) => `> ${l}`).join("\n");
  return `\n\nOn ${when}, ${who} wrote:\n${body}\n`;
}


/* ─── HTML body viewer (sandboxed) ────────────────────────────
 * Emails routinely carry hostile HTML.  A sandboxed iframe with
 * no scripts + a restrictive srcdoc is the cheapest, safest
 * option — we do not need remote images, JS, or forms in the
 * reader.  All links open in a new tab.                        */
function HtmlBody({ html }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const doc = el.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><base target="_blank">
      <style>
        body{margin:0;padding:12px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:13.5px;color:#0f172a;line-height:1.55;word-wrap:break-word}
        img{max-width:100%;height:auto}
        a{color:#6d28d9}
        blockquote{border-left:3px solid #e2e8f0;margin:0 0 0 6px;padding:2px 10px;color:#475569}
      </style></head><body>${html || ""}</body></html>`);
    doc.close();
    // Autosize the iframe to its content up to a generous cap.
    const resize = () => {
      try {
        const h = Math.min(doc.body.scrollHeight + 24, 720);
        el.style.height = `${h}px`;
      } catch { /* noop */ }
    };
    resize();
    const t = setTimeout(resize, 250);
    return () => clearTimeout(t);
  }, [html]);
  return (
    <iframe
      ref={ref}
      title="Email body"
      sandbox="allow-same-origin allow-popups"
      className="w-full rounded-md ring-1 ring-slate-200 bg-white"
      style={{ height: 240 }}
      data-testid="mail-body-html"
    />
  );
}


function TextBody({ text }) {
  return (
    <pre
      className="whitespace-pre-wrap break-words text-[13px] text-slate-800 leading-relaxed font-sans px-3 py-2 rounded-md bg-slate-50 ring-1 ring-slate-200"
      data-testid="mail-body-text"
    >{text || "(no body)"}</pre>
  );
}


function AttachmentList({ messageId, attachments }) {
  if (!attachments || attachments.length === 0) return null;
  const visible = attachments.filter((a) => !a.inline);
  if (visible.length === 0) return null;
  return (
    <div className="mt-3 space-y-1" data-testid="mail-attachments">
      <p className="text-[10.5px] uppercase tracking-[0.10em] font-bold text-slate-500 flex items-center gap-1">
        <Paperclip size={10} strokeWidth={2.6} /> Attachments · {visible.length}
      </p>
      <ul className="flex flex-wrap gap-1.5">
        {visible.map((a) => (
          <li key={a.attachment_id} data-testid={`mail-attachment-${a.attachment_id}`}>
            <a
              href={`${BACKEND}/api/v2/mailbox/${messageId}/attachments/${encodeURIComponent(a.attachment_id)}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md ring-1 ring-slate-200 bg-white px-2 py-1 text-[12px] text-slate-700 hover:bg-slate-50 hover:ring-slate-300"
              data-testid={`mail-attachment-download-${a.attachment_id}`}
              title={`Download ${a.filename}`}
            >
              <Download size={11} strokeWidth={2.4} className="text-violet-600" />
              <span className="truncate max-w-[220px]">{a.filename}</span>
              <span className="text-[10.5px] text-slate-400 tabular-nums">{_formatSize(a.size)}</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}


function MessageBlock({ message, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const hasHtml = !!message.body_html;
  return (
    <div className="rounded-lg ring-1 ring-slate-200 bg-white" data-testid={`mail-msg-${message.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-3 text-left px-3 py-2.5 hover:bg-slate-50 rounded-lg"
        aria-expanded={open}
        data-testid={`mail-msg-toggle-${message.id}`}
      >
        <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-700 text-[12px] font-bold flex items-center justify-center shrink-0">
          {(message.from_name || message.from_email || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-slate-900 truncate">
              {message.from_name || message.from_email}
            </span>
            <span className="text-[11px] text-slate-500 truncate">&lt;{message.from_email}&gt;</span>
          </div>
          <p className="mt-0.5 text-[11.5px] text-slate-500 truncate">
            to {(message.to_emails || []).map((t) => t.email || t.name).filter(Boolean).join(", ") || "—"}
            {(message.cc_emails || []).length ? ` · cc ${(message.cc_emails || []).map((c) => c.email).filter(Boolean).join(", ")}` : ""}
          </p>
        </div>
        <span className="text-[11px] text-slate-400 tabular-nums shrink-0" title={message.received_at}>
          {_fmtWhen(message.received_at)}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3" data-testid={`mail-msg-body-${message.id}`}>
          {hasHtml ? <HtmlBody html={message.body_html} /> : <TextBody text={message.body_text} />}
          <AttachmentList messageId={message.id} attachments={message.attachments} />
        </div>
      )}
    </div>
  );
}


function Composer({ mode, sourceMessage, onCancel, onSent }) {
  // mode ∈ 'reply' | 'reply_all' | 'forward'
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!sourceMessage) return;
    const s = sourceMessage;
    if (mode === "reply") {
      setTo(s.from_email || "");
      setCc("");
    } else if (mode === "reply_all") {
      setTo(s.from_email || "");
      setCc((s.cc_emails || []).map((c) => c.email).filter(Boolean).join(", "));
    } else if (mode === "forward") {
      setTo("");
      setCc("");
    }
    const subj = (s.subject || "").trim();
    if (mode === "forward") {
      setSubject(subj.toLowerCase().startsWith("fwd:") ? subj : `Fwd: ${subj}`);
    } else {
      setSubject(subj.toLowerCase().startsWith("re:") ? subj : `Re: ${subj}`);
    }
    setBody(_quoteBody(s));
  }, [mode, sourceMessage]);

  const send = useCallback(async () => {
    if (!to.trim()) { toast.error("At least one recipient is required."); return; }
    setSending(true);
    try {
      const payload = {
        action: mode,
        reply_to_email_id: sourceMessage?.id,
        to: to.split(",").map((s) => s.trim()).filter(Boolean),
        cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
        subject: subject || "(no subject)",
        body_text: body,
      };
      await api.post(`/api/v2/mailbox/send`, payload);
      toast.success(mode === "forward" ? "Forwarded." : "Reply sent.");
      onSent?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  }, [to, cc, subject, body, mode, sourceMessage, onSent]);

  const label = mode === "forward" ? "Forward"
    : mode === "reply_all" ? "Reply all"
    : "Reply";

  return (
    <div className="mt-3 rounded-lg ring-1 ring-violet-200 bg-violet-50/40 p-3" data-testid="mail-composer">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[12.5px] font-bold text-violet-900 uppercase tracking-[0.06em]">{label}</h4>
        <button type="button" onClick={onCancel} className="text-slate-500 hover:text-slate-700" data-testid="mail-composer-cancel" aria-label="Cancel">
          <X size={14} strokeWidth={2.4} />
        </button>
      </div>
      <div className="space-y-2">
        <label className="block">
          <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">To</span>
          <input
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="name@example.com, another@example.com"
            className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
            data-testid="mail-composer-to"
          />
        </label>
        {(mode === "reply_all" || mode === "forward" || cc) && (
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Cc</span>
            <input
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="optional"
              className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
              data-testid="mail-composer-cc"
            />
          </label>
        )}
        <label className="block">
          <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
            data-testid="mail-composer-subject"
          />
        </label>
        <label className="block">
          <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Message</span>
          <textarea
            rows={8}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-2 text-[13px] font-sans focus:outline-none focus:ring-2 focus:ring-violet-300"
            data-testid="mail-composer-body"
          />
        </label>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={sending}
          className="px-3 py-1.5 rounded-md text-[12.5px] font-semibold text-slate-600 hover:bg-slate-100"
          data-testid="mail-composer-discard"
        >Discard</button>
        <button
          type="button"
          onClick={send}
          disabled={sending || !to.trim()}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[12.5px] font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
          data-testid="mail-composer-send"
        >
          {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} strokeWidth={2.4} />}
          {sending ? "Sending…" : `Send ${label.toLowerCase()}`}
        </button>
      </div>
    </div>
  );
}


/**
 * MailReader — the drawer entry point.
 *
 * Props
 *   message  — seed row from the inbox list (id + optional
 *              thread_id).  When falsy, the drawer is hidden.
 *   onClose  — called when the operator dismisses the drawer.
 *   onReadStateChanged — called after mark-as-read succeeds so
 *              the parent inbox can flip the unread dot.
 */
export default function MailReader({ message, onClose, onReadStateChanged, initialMode = null }) {
  const [loading, setLoading] = useState(false);
  const [thread, setThread] = useState([]);      // list of EmailOut
  const [seed, setSeed] = useState(null);        // enriched seed message
  const [composerMode, setComposerMode] = useState(null); // 'reply' | 'reply_all' | 'forward' | null

  // Load: single message + (optional) full thread.
  useEffect(() => {
    if (!message?.id) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setComposerMode(initialMode);  // e.g. "reply" from a Deal Room deep-link; sending stays explicit
      try {
        // Always hydrate the seed via /message/{id} so we have
        // body_html + attachments (the inbox list omits those to
        // keep the payload small).
        const { data: enriched } = await api.get(`/api/v2/mailbox/message/${message.id}`);
        if (!alive) return;
        setSeed(enriched);
        // Mark as read (best-effort — a 404 here would be a
        // race between disconnect and open, so we swallow it).
        if (enriched.unread) {
          try {
            await api.post(`/api/v2/mailbox/${message.id}/read`);
            onReadStateChanged?.(message.id);
          } catch { /* noop */ }
        }
        if (enriched.thread_id) {
          try {
            const { data: rows } = await api.get(`/api/v2/mailbox/thread/${encodeURIComponent(enriched.thread_id)}`);
            if (!alive) return;
            const list = Array.isArray(rows) ? rows : [];
            // Ensure the seed is in the list (thread endpoint
            // returns everything with matching threadId — will
            // include seed but merge defensively).
            const seen = new Set(list.map((r) => r.id));
            if (!seen.has(enriched.id)) list.push(enriched);
            list.sort((a, b) => (a.received_at || "").localeCompare(b.received_at || ""));
            setThread(list);
          } catch {
            setThread([enriched]);
          }
        } else {
          setThread([enriched]);
        }
      } catch (e) {
        toast.error(apiError(e));
        onClose?.();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [message?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const latest = useMemo(() => (thread.length ? thread[thread.length - 1] : seed), [thread, seed]);
  const composerSource = latest || seed;

  if (!message?.id) return null;
  const simulated = isSimulatedDemo(seed || message);
  return createPortal(
    <div className="fixed inset-0 z-[60] flex h-screen" data-testid="mail-reader-overlay">
      <div className="flex-1 bg-slate-900/30" onClick={onClose} aria-hidden />
      <aside
        className="w-full max-w-2xl h-full bg-slate-50 shadow-2xl flex flex-col"
        role="dialog"
        aria-label="Read email"
        data-testid="mail-reader"
      >
        {/* Header */}
        <header className="flex items-start gap-3 px-4 py-3 bg-white ring-1 ring-slate-200">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="text-[15px] font-bold text-slate-900 truncate" data-testid="mail-reader-subject">
                {seed?.subject || message.subject || "(no subject)"}
              </h3>
              {simulated && <DemoBadge testid="mail-reader-demo-badge" />}
            </div>
            <p className="mt-0.5 text-[11.5px] text-slate-500">
              {thread.length > 1 ? `${thread.length} messages in thread` : "1 message"}
            </p>
          </div>
          {seed?.deal_id && (
            <a
              href={`/deal-rooms/${seed.deal_id}`}
              className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-violet-700 hover:underline shrink-0"
              data-testid="mail-reader-deal-link"
            >
              Open Deal Room <ExternalLink size={10} strokeWidth={2.6} />
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"
            data-testid="mail-reader-close"
            aria-label="Close reader"
          >
            <X size={16} strokeWidth={2.4} />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="flex items-center gap-2 text-[12.5px] text-slate-500" data-testid="mail-reader-loading">
              <Loader2 size={12} className="animate-spin" /> Loading conversation…
            </div>
          ) : (
            <div className="space-y-2" data-testid="mail-reader-thread">
              {thread.map((msg, idx) => (
                <MessageBlock key={msg.id} message={msg} defaultOpen={idx === thread.length - 1} />
              ))}
            </div>
          )}

          {/* Composer */}
          {!loading && !simulated && composerMode && composerSource && (
            <Composer
              mode={composerMode}
              sourceMessage={composerSource}
              onCancel={() => setComposerMode(null)}
              onSent={() => { setComposerMode(null); onClose?.(); }}
            />
          )}
        </div>

        {/* Footer actions */}
        {!loading && simulated && (
          <footer className="px-4 py-3 bg-white ring-1 ring-slate-200 text-[11.5px] text-slate-500" data-testid="mail-reader-demo-note">
            Simulated demo email — replying and forwarding are turned off.
          </footer>
        )}
        {!loading && !composerMode && !simulated && (
          <footer className="flex items-center gap-2 px-4 py-3 bg-white ring-1 ring-slate-200" data-testid="mail-reader-actions">
            <button
              type="button"
              onClick={() => setComposerMode("reply")}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md ring-1 ring-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="mail-reader-reply"
            >
              <Reply size={12} strokeWidth={2.4} /> Reply
            </button>
            <button
              type="button"
              onClick={() => setComposerMode("reply_all")}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md ring-1 ring-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="mail-reader-reply-all"
            >
              <ReplyAll size={12} strokeWidth={2.4} /> Reply all
            </button>
            <button
              type="button"
              onClick={() => setComposerMode("forward")}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md ring-1 ring-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="mail-reader-forward"
            >
              <Forward size={12} strokeWidth={2.4} /> Forward
            </button>
          </footer>
        )}
      </aside>
    </div>,
    document.body,
  );
}
